// @need-translate
import {t} from '../../../common/getTranslations';
import {openWindow} from '../../../common/openWindow.function';
import {formatWithCommas} from '../../../common/shared/utils/formatWithCommas';
import {getTicketNameForLogging} from '../../../domain/buy/ticket/util/getTicketNameForLogging';
import {
  Maybe,
  SaleMethod,
  SimpleSet,
  TicketInfo,
  TicketKind,
  TicketType,
} from '../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {ButtonV2Variant} from '../../atoms/ButtonV2/ButtonV2.enum';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Text} from '../../atoms/Text/Text';
import {Dialog} from '../Dialog';
import {DialogButtonProps} from '../Dialog/DialogButton';

export interface TicketNonstopWatchingSelectTicketDialogProps {
  isMobile: boolean;
  seriesId: number;
  productId: number;
  saleMethod?: Maybe<SaleMethod>;
  availableTicketRentalType?: Maybe<TicketType>;
  purchasableTicketRental?: Maybe<TicketInfo>;
  availableTicketOwnType?: Maybe<TicketType>;
  purchasableTicketOwn?: Maybe<TicketInfo>;
  onSelectTicket: (ticketInfo: TicketInfo) => void;
  onClickCancel: () => void;
  set?: Maybe<SimpleSet>;
  onClickSet?: (params: {setInfo: SimpleSet}) => void;
}

export function TicketNonstopWatchingSelectTicketDialog({
  isMobile,
  seriesId,
  productId,
  saleMethod,
  availableTicketRentalType,
  purchasableTicketRental,
  availableTicketOwnType,
  purchasableTicketOwn,
  onSelectTicket,
  onClickCancel,
  set,
  onClickSet,
}: TicketNonstopWatchingSelectTicketDialogProps) {
  const isCashType = saleMethod === SaleMethod.Cash;
  return (
    <Dialog
      isOpen={true}
      onClose={onClickCancel}
      isUsingScrollBlock={false}
      isMobile={isMobile}
      {...putTiaraObjectProps({
        click: {
          layer1: `${isCashType ? '캐시타입' : ''}정주행구매팝업`,
          imp_id: `popup_bp${isCashType ? '_cash' : ''}`,
          imp_provider: 'service',
        },
        viewimp_contents: {
          layer1: `${isCashType ? '캐시타입' : ''}정주행구매팝업`,
          imp_id: `popup_bp${isCashType ? '_cash' : ''}`,
          imp_provider: 'service',
          imp_type: 'vimp',
        },
        customProps: {
          popup_type: `${isCashType ? '캐시타입' : ''}정주행구매팝업`,
          event_episode_id: String(productId),
          page_ticket_rental: availableTicketRentalType
            ? getTicketNameForLogging(availableTicketRentalType)
            : purchasableTicketRental
              ? `3일대여_${purchasableTicketRental.discountPrice ?? purchasableTicketRental.price}캐시`
              : '',
          page_ticket_own: availableTicketOwnType
            ? getTicketNameForLogging(availableTicketOwnType)
            : purchasableTicketOwn
              ? `소장_${purchasableTicketOwn.discountPrice ?? purchasableTicketOwn.price}캐시`
              : '',
          viewer_mode: '정주행모드',
        },
      })}
      onInView={target => {
        aggregateAndSendLog({
          targetElem: target as HTMLElement,
          actionType: 'ViewImp',
          actionName: '팝업_노출',
          customProps: {
            user_action: 'imp',
          },
        });
      }}
    >
      <Dialog.Dimmed />
      <Dialog.Title>{t('viewer_binge_watch_buy_popup')}</Dialog.Title>
      <Dialog.List>
        <Dialog.Label>
          {isCashType
            ? t('viewer_binge_watch_buy_episode_popup_message')
            : t('viewer_binge_watch_buy_popup_message')}
        </Dialog.Label>
      </Dialog.List>
      <Dialog.List>
        <Dialog.Divider />
      </Dialog.List>
      <Dialog.List>
        <div
          className="font-small1 text-theme-solid-60 flex items-center justify-center"
          onClick={e =>
            LOG_CLICK_EVENT(
              isCashType ? '콘텐츠구매안내_클릭' : '이용약관_클릭',
              e,
              () => {
                openWindow({
                  url: `https://${window.location.host}/content/policy/${
                    isCashType ? 'cash' : 'ticket'
                  }`,
                  target: `${isCashType ? '콘텐츠' : '이용권'} 구매 안내`,
                  features: 'width=360,height=800',
                });
              }
            )
          }
        >
          <Dialog.Label className="font-small1 text-theme-solid-60">
            {isCashType
              ? t('purchase_cash_popup_terms_button')
              : t('purchase_ticket_popup_ticketbuy_info_button')}
          </Dialog.Label>
          <Icon type={IconType.ContentHomeHelpSolid} className="ml-4pxr" />
        </div>
      </Dialog.List>

      <Dialog.List className="px-18pxr pb-10pxr space-y-10pxr">
        {isCashType && set && (
          <Dialog.Button
            data-cy="cash-type-set-button"
            variant={ButtonV2Variant.Negative}
            onClick={e =>
              onClickSet &&
              LOG_CLICK_EVENT('세트상품더보기_클릭', e, () =>
                onClickSet({setInfo: set})
              )
            }
          >
            {!set.discountRate && t('contenthome_sethome_button_no_discount')}
            {!!set.discountRate && (
              <div>
                {t('purchase_cash_popup_sethome_button', {
                  values: {value: set.discountRate},
                })}
              </div>
            )}
          </Dialog.Button>
        )}

        {purchasableTicketRental && !!purchasableTicketRental.ticketId && (
          <Dialog.Button
            {...getSelectTicketProps({
              ticketKind: TicketKind.Rent,
              ticketInfo: purchasableTicketRental,
            })}
          />
        )}
        {purchasableTicketOwn && !!purchasableTicketOwn.ticketId && (
          <Dialog.Button
            {...getSelectTicketProps({
              ticketKind: TicketKind.Own,
              ticketInfo: purchasableTicketOwn,
            })}
          />
        )}
      </Dialog.List>
      <Dialog.BottomCombination>
        <Dialog.Button
          onClick={e => LOG_CLICK_EVENT('취소_클릭', e, onClickCancel)}
        >
          {t('common_cancel_button')}
        </Dialog.Button>
      </Dialog.BottomCombination>
    </Dialog>
  );
  function getSelectTicketProps({
    ticketKind,
    ticketInfo,
  }: {
    ticketKind: TicketKind;
    ticketInfo: TicketInfo;
  }): DialogButtonProps {
    return {
      description: (
        <Text>
          {ticketInfo?.discountPrice && (
            <>
              <Text className="text-theme-solid-60 line-through">
                {formatWithCommas(ticketInfo.price)}
              </Text>
              &nbsp;
            </>
          )}
          <Text>
            {t('purchase_cash_popup_cash_button', {
              values: {
                value: formatWithCommas(
                  ticketInfo.discountPrice ?? ticketInfo.price
                ),
              },
            })}
          </Text>
        </Text>
      ),
      ...putTiaraObjectProps({
        customProps: {
          event_series_id: String(seriesId),
          event_ticket_type: `${
            ticketKind === TicketKind.Own ? '소장' : '대여'
          }_${ticketInfo.discountPrice ?? ticketInfo.price}캐시`,
        },
      }),
      onClick: e =>
        LOG_CLICK_EVENT(
          `${ticketKind === TicketKind.Own ? '소장' : '대여'}_클릭`,
          e,
          () => onSelectTicket(ticketInfo)
        ),
      children: isCashType
        ? ticketKind === TicketKind.Own
          ? t('common_content_possession')
          : t('purchase_cash_popup_rent', {
              values: {
                value: t('common_period_day', {values: {value: 3}}),
              },
            })
        : ticketKind === TicketKind.Own
          ? t('common_ticket_possession')
          : t('purchase_ticket_popup_rental_button', {
              values: {
                value: t('common_period_day', {values: {value: 3}}),
              },
            }),
      variant: ButtonV2Variant.Primary,
    };
  }
}
